import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { configurationApi } from '../api/configuration.api';
import { PeriodeSelector } from '../../bulletins/components/PeriodeSelector';

/* ── Types locaux ─────────────────────────────────────────────────────────── */
interface Tranche {
  ordre: number;
  seuilMin: number;
  seuilMax: number | null;
  taux: number;
  deductionFixe: number;
}

interface ConfigPeriode {
  cnss: { plafondMensuel: number; tauxEmploye: number; tauxPatronal: number };
  vps: { taux: number };
  its: { tranches: Tranche[] };
}

/* ── Taux par défaut (seed Bénin) ─────────────────────────────────────────── */
const CONFIG_DEFAUT: ConfigPeriode = {
  cnss: { plafondMensuel: 500_000, tauxEmploye: 3.6, tauxPatronal: 16.4 },
  vps: { taux: 1 },
  its: {
    tranches: [
      { ordre: 1, seuilMin: 0,       seuilMax: 60_000,  taux: 0,  deductionFixe: 0      },
      { ordre: 2, seuilMin: 60_001,  seuilMax: 130_000, taux: 10, deductionFixe: 6_000  },
      { ordre: 3, seuilMin: 130_001, seuilMax: 240_000, taux: 15, deductionFixe: 12_500 },
      { ordre: 4, seuilMin: 240_001, seuilMax: 400_000, taux: 19, deductionFixe: 22_100 },
      { ordre: 5, seuilMin: 400_001, seuilMax: null,    taux: 30, deductionFixe: 66_100 },
    ],
  },
};

/* ── Calcul ITS par barème progressif ────────────────────────────────────── */
function calculITS(brut: number, tranches: Tranche[]): number {
  const sorted = [...tranches].sort((a, b) => a.ordre - b.ordre);
  for (const t of sorted) {
    if (brut >= t.seuilMin && (t.seuilMax === null || brut <= t.seuilMax)) {
      return Math.max(0, Math.floor((brut * t.taux) / 100) - t.deductionFixe);
    }
  }
  return 0;
}

/* ── Formatage ───────────────────────────────────────────────────────────── */
const f   = (n: number) => n.toLocaleString('fr-FR') + ' F';
const pct = (n: number) => `${n} %`;
const int = (s: string) => Math.max(0, parseInt(s.replace(/\s/g, ''), 10) || 0);

/* ── Champ de saisie (défini hors du composant pour éviter la perte de focus) ── */
interface ChampProps {
  label: string;
  cle: string;
  value: string;
  required?: boolean;
  onChange: (cle: string, value: string) => void;
}

function Champ({ label, cle, value, required = false, onChange }: ChampProps) {
  return (
    <div>
      <label className="block text-xs font-medium text-content-muted uppercase tracking-wide mb-1.5">
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      <input
        type="number"
        min="0"
        value={value}
        onChange={e => onChange(cle, e.target.value)}
        placeholder="0"
        className="w-full border border-border rounded-xl px-4 py-2.5 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-surface text-content"
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
export function SimulateurPage() {
  const now = new Date();
  const [mois, setMois]               = useState(now.getMonth() + 1);
  const [annee, setAnnee]             = useState(now.getFullYear());
  const [config, setConfig]           = useState<ConfigPeriode | null>(null);
  const [configErreur, setConfigErreur] = useState(false);
  const [chargement, setChargement]   = useState(false);

  const [saisie, setSaisie] = useState({
    salaireBase:      '',
    primes:           '',
    indemnites:       '',
    avanceAcompte:    '',
    oppositions:      '',
    taxesTemporaires: '',
  });

  /* ── Chargement de la config à chaque changement de période ── */
  const handlePeriode = async (m: number, a: number) => {
    setMois(m);
    setAnnee(a);
    setChargement(true);
    setConfigErreur(false);
    try {
      const data = await configurationApi.obtenirPeriode(a, m) as any;
      // Le backend retourne itsConfig (pas its) — on mappe vers la structure locale
      const config: ConfigPeriode = {
        cnss: data.cnss,
        vps: data.vps,
        its: { tranches: data.itsConfig?.tranches ?? data.its?.tranches ?? [] },
      };
      if (!config.its.tranches.length) throw new Error('Tranches ITS manquantes');
      setConfig(config);
    } catch {
      setConfigErreur(true);
      setConfig(null);
    } finally {
      setChargement(false);
    }
  };

  const cfg = config ?? CONFIG_DEFAUT;

  /* ── Calculs en temps réel ── */
  const calcul = useMemo(() => {
    const salaireBase      = int(saisie.salaireBase);
    const primes           = int(saisie.primes);
    const indemnites       = int(saisie.indemnites);
    const avanceAcompte    = int(saisie.avanceAcompte);
    const oppositions      = int(saisie.oppositions);
    const taxesTemporaires = int(saisie.taxesTemporaires);

    const salaireBrut  = salaireBase + primes + indemnites;
    const baseCNSS     = Math.min(salaireBrut, cfg.cnss.plafondMensuel);
    const cnssEmploye  = Math.floor((baseCNSS * cfg.cnss.tauxEmploye)  / 100);
    const cnssPatronal = Math.floor((baseCNSS * cfg.cnss.tauxPatronal) / 100);
    const vps          = Math.floor((salaireBrut * cfg.vps.taux)        / 100);
    const baseImposableITS = Math.floor(salaireBrut / 1000) * 1000;
    const its          = calculITS(baseImposableITS, cfg.its.tranches);
    const salaireNet   = salaireBrut - cnssEmploye - its;
    const netAPayer    = salaireNet - avanceAcompte - oppositions - taxesTemporaires;

    return {
      salaireBase, primes, indemnites, avanceAcompte, oppositions, taxesTemporaires,
      salaireBrut, baseCNSS, cnssEmploye, cnssPatronal, vps, its,
      salaireNet, netAPayer, baseImposableITS,
    };
  }, [saisie, cfg]);

  /* ── Lignes du tableau de résultats ── */
  const lignes: Array<{
    label: string;
    formule: string;
    montant: number;
    positif: boolean;
    isSeparator?: boolean;
    info?: boolean;
  }> = [
    { label: 'Salaire de base',  formule: 'Saisie directe',                                           montant: calcul.salaireBase,   positif: true  },
    ...(calcul.primes      > 0 ? [{ label: 'Primes',      formule: 'Saisie directe', montant: calcul.primes,      positif: true  }] : []),
    ...(calcul.indemnites  > 0 ? [{ label: 'Indemnités',  formule: 'Saisie directe', montant: calcul.indemnites,  positif: true  }] : []),
    { label: 'Salaire brut',     formule: 'Base + Primes + Indemnités',                               montant: calcul.salaireBrut,   positif: true,  isSeparator: true },
    { label: 'CNSS salariale',   formule: `${pct(cfg.cnss.tauxEmploye)} × ${calcul.baseCNSS.toLocaleString('fr-FR')}`, montant: calcul.cnssEmploye, positif: false },
    { label: 'ITS',              formule: `Barème progressif — base ${calcul.baseImposableITS.toLocaleString('fr-FR')} F`,                montant: calcul.its,           positif: false },
    { label: 'Salaire net',      formule: 'Brut − CNSS − ITS',                                        montant: calcul.salaireNet,    positif: true,  isSeparator: true },
    ...(calcul.avanceAcompte    > 0 ? [{ label: 'Avance / Acompte',    formule: 'Saisie directe', montant: calcul.avanceAcompte,    positif: false }] : []),
    ...(calcul.oppositions      > 0 ? [{ label: 'Oppositions',         formule: 'Saisie directe', montant: calcul.oppositions,      positif: false }] : []),
    ...(calcul.taxesTemporaires > 0 ? [{ label: 'Taxes temporaires',   formule: 'Saisie directe', montant: calcul.taxesTemporaires, positif: false }] : []),
    ...(calcul.cnssPatronal     > 0 ? [{ label: 'CNSS patronale (info)', formule: `${pct(cfg.cnss.tauxPatronal)} × ${calcul.baseCNSS.toLocaleString('fr-FR')}`, montant: calcul.cnssPatronal, positif: true, info: true }] : []),
    ...(calcul.vps > 0 ? [{ label: 'VPS (patronal)', formule: `${pct(cfg.vps.taux)} × Brut`, montant: calcul.vps, positif: true, info: true }] : []),
  ];

  const handleChamp = (cle: string, value: string) =>
    setSaisie(s => ({ ...s, [cle]: value }));

  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* En-tête */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-content">Simulateur de paie</h1>
          <p className="text-content-subtle mt-1 text-sm">
            Estimez le net à payer en temps réel selon les paramètres de la période sélectionnée.
          </p>
        </div>
        <Link
          to="/paie/configuration"
          className="text-sm text-primary-text border border-primary px-4 py-2 rounded-xl hover:bg-primary-light transition-colors flex-shrink-0"
        >
          Modifier les paramètres →
        </Link>
      </div>

      {/* Sélecteur de période + statut config */}
      <div className="flex items-center gap-4 flex-wrap mb-6 p-4 bg-surface border border-border rounded-2xl shadow-sm">
        <span className="text-sm font-medium text-content-muted flex-shrink-0">
          Période de référence :
        </span>
        <PeriodeSelector mois={mois} annee={annee} onChange={handlePeriode} />
        {chargement && (
          <span className="text-xs text-content-subtle animate-pulse">
            Chargement de la configuration…
          </span>
        )}
        {!chargement && configErreur && (
          <span className="flex items-center gap-1.5 text-xs text-warning bg-warning-light border border-warning px-3 py-1.5 rounded-full">
            ⚠ Configuration indisponible — taux par défaut appliqués
          </span>
        )}
        {!chargement && config && !configErreur && (
          <span className="text-xs text-success bg-success-light border border-success px-3 py-1.5 rounded-full">
            ✓ Configuration chargée
          </span>
        )}
      </div>

      {/* Layout 2 colonnes asymétriques */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Formulaire (2/5) ── */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border bg-surface-raised">
              <h2 className="text-xs font-semibold text-content-subtle uppercase tracking-widest">
                Éléments de rémunération
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <Champ label="Salaire de base (FCFA)" cle="salaireBase" value={saisie.salaireBase} onChange={handleChamp} required />
              <Champ label="Primes (FCFA)"           cle="primes"      value={saisie.primes}      onChange={handleChamp} />
              <Champ label="Indemnités (FCFA)"       cle="indemnites"  value={saisie.indemnites}  onChange={handleChamp} />
            </div>

            <div className="px-6 pb-2">
              <div className="border-t border-border pt-3 pb-1">
                <p className="text-xs font-semibold text-content-subtle uppercase tracking-widest">
                  Déductions complémentaires
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 space-y-4">
              <Champ label="Avance / Acompte"   cle="avanceAcompte"    value={saisie.avanceAcompte}    onChange={handleChamp} />
              <Champ label="Oppositions"         cle="oppositions"      value={saisie.oppositions}      onChange={handleChamp} />
              <Champ label="Taxes temporaires"   cle="taxesTemporaires" value={saisie.taxesTemporaires} onChange={handleChamp} />
            </div>
          </div>

          {/* Taux appliqués */}
          <div className="bg-surface-raised border border-border rounded-2xl p-5">
            <p className="text-xs font-semibold text-content-subtle uppercase tracking-widest mb-3">
              Taux appliqués
            </p>
            <div className="space-y-2.5">
              {[
                { label: 'CNSS salariale',  value: pct(cfg.cnss.tauxEmploye) },
                { label: 'CNSS patronale',  value: pct(cfg.cnss.tauxPatronal) },
                { label: 'Plafond CNSS',    value: f(cfg.cnss.plafondMensuel) },
                { label: 'VPS',             value: pct(cfg.vps.taux) },
                { label: 'ITS',             value: 'Barème progressif' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-xs text-content-subtle">{label}</span>
                  <span className="text-xs font-mono font-medium text-content-muted">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Résultats (3/5) ── */}
        <div className="lg:col-span-3">
          <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border bg-surface-raised">
              <h2 className="text-xs font-semibold text-content-subtle uppercase tracking-widest">
                Résultats en temps réel
              </h2>
            </div>

            <table className="w-full">
              <thead className="bg-surface-raised">
                <tr>
                  {['Élément', 'Formule', 'Montant'].map(h => (
                    <th
                      key={h}
                      className="px-5 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider last:text-right"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lignes.map((ligne, i) => (
                  <tr
                    key={i}
                    className={`transition-colors ${
                      ligne.info
                        ? 'opacity-50'
                        : ligne.isSeparator
                        ? 'bg-surface-raised'
                        : 'hover:bg-surface-raised'
                    }`}
                  >
                    <td
                      className={`px-5 py-3 text-sm ${
                        ligne.isSeparator
                          ? 'font-semibold text-content'
                          : ligne.info
                          ? 'text-content-subtle italic text-xs'
                          : 'text-content-muted'
                      }`}
                    >
                      {ligne.label}
                    </td>
                    <td
                      className={`px-5 py-3 text-xs font-mono text-content-subtle ${
                        ligne.info ? 'italic' : ''
                      }`}
                    >
                      {ligne.formule}
                    </td>
                    <td
                      className={`px-5 py-3 text-right font-mono tabular-nums font-semibold transition-all ${
                        ligne.isSeparator
                          ? 'text-content text-base'
                          : ligne.info
                          ? 'text-content-subtle text-xs'
                          : ligne.positif
                          ? 'text-content-muted'
                          : 'text-danger'
                      }`}
                    >
                      {ligne.positif ? '' : '−'}
                      {f(Math.abs(ligne.montant))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── NET À PAYER — pièce maîtresse ── */}
            <div className="p-5 bg-gradient-to-br from-indigo-600 to-indigo-700 border-t border-indigo-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-indigo-200">
                    Net à payer
                  </p>
                  <p className="text-xs text-indigo-300 mt-0.5">
                    Résultat final de la simulation
                  </p>
                </div>
                <div className="text-right">
                  <p
                    className={`text-4xl font-bold font-mono tabular-nums tracking-tight transition-all ${
                      calcul.netAPayer < 0 ? 'text-rose-300' : 'text-white'
                    }`}
                  >
                    {f(Math.max(0, calcul.netAPayer))}
                  </p>
                  {calcul.netAPayer < 0 && (
                    <p className="text-xs text-rose-300 mt-1">
                      ⚠ Montant négatif — vérifiez les déductions
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Barème ITS */}
          <details className="mt-4 bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
            <summary className="px-5 py-3 text-xs font-semibold text-content-subtle uppercase tracking-widest cursor-pointer bg-surface-raised hover:bg-surface-overlay transition-colors select-none">
              Barème ITS (tranches appliquées)
            </summary>
            <table className="w-full">
              <thead>
                <tr className="border-t border-border">
                  <th className="px-5 py-2 text-left text-xs text-content-subtle font-medium">De</th>
                  <th className="px-5 py-2 text-left text-xs text-content-subtle font-medium">À</th>
                  <th className="px-5 py-2 text-right text-xs text-content-subtle font-medium">Taux</th>
                  <th className="px-5 py-2 text-right text-xs text-content-subtle font-medium">Déduction fixe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[...cfg.its.tranches]
                  .sort((a, b) => a.ordre - b.ordre)
                  .map(t => {
                    const actif =
                      calcul.salaireBrut >= t.seuilMin &&
                      (t.seuilMax === null || calcul.salaireBrut <= t.seuilMax);
                    return (
                      <tr
                        key={t.ordre}
                        className={`transition-colors ${actif ? 'bg-primary-light' : 'hover:bg-surface-raised'}`}
                      >
                        <td className={`px-5 py-2 text-xs font-mono ${actif ? 'text-primary-text font-semibold' : 'text-content-subtle'}`}>
                          {t.seuilMin.toLocaleString('fr-FR')} F
                        </td>
                        <td className={`px-5 py-2 text-xs font-mono ${actif ? 'text-primary-text font-semibold' : 'text-content-subtle'}`}>
                          {t.seuilMax !== null ? t.seuilMax.toLocaleString('fr-FR') + ' F' : '∞'}
                        </td>
                        <td className={`px-5 py-2 text-xs font-mono text-right ${actif ? 'text-primary-text font-semibold' : 'text-content-subtle'}`}>
                          {t.taux} %
                        </td>
                        <td className={`px-5 py-2 text-xs font-mono text-right ${actif ? 'text-primary-text font-semibold' : 'text-content-subtle'}`}>
                          {t.deductionFixe.toLocaleString('fr-FR')} F
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </details>
        </div>
      </div>
    </div>
  );
}
